import { useEffect, useState } from "react";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { updateCustomSnippetsUrl } from "../services/updateCustomSnippetsUrl";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Input } from "@libs/zapi-ui/ds/components/input";
import { Settings, Edit2, Trash2, Check, X } from "lucide-react";

export function CustomSnippetsUrlSection() {
  const { t } = useTranslation("settings");
  const { isLabs, customSnippetsUrl, setMe } = useMeStore();
  const [url, setUrl] = useState(customSnippetsUrl || "");
  const [isEditing, setIsEditing] = useState(false);

  const mutation = useMutation({
    mutationFn: updateCustomSnippetsUrl,
    onSuccess: (response) => {
      toast.success(t("snippets.updated"));
      setMe({ customSnippetsUrl: response.data.customSnippetsUrl });
      setIsEditing(false);
    },
    onError: () => {
      toast.error(t("snippets.updateFailed"));
      setUrl(customSnippetsUrl || "");
    }
  });

  useEffect(() => {
    setUrl(customSnippetsUrl || "");

    return () => {
      setUrl(customSnippetsUrl || "");
    };
  }, [customSnippetsUrl]);

  const handleSave = () => {
    const urlToSave = url.trim() === "" ? null : url.trim();
    mutation.mutate({ customSnippetsUrl: urlToSave });
  };

  const handleCancel = () => {
    setUrl(customSnippetsUrl || "");
    setIsEditing(false);
  };

  const handleClear = () => {
    setUrl("");
    mutation.mutate({ customSnippetsUrl: null });
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const isValidUrl = (urlString: string) => {
    try {
      new URL(urlString);
      return true;
    } catch {
      return false;
    }
  };

  if (!isLabs) {
    return null;
  }

  return (
    <div className="border border-zinc-200 rounded-xl p-6 space-y-4 bg-linear-to-br from-white to-zinc-50/50">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Settings className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <p className="typo-label-lg font-semibold text-zinc-900">{t("snippets.heading")}</p>
          <p className="typo-body-sm text-innoveraZinc-600 mt-1">{t("snippets.description")}</p>
        </div>
      </div>

      {!isEditing ? (
        <div className="space-y-4">
          <div className="min-h-[50px] flex items-center">
            {customSnippetsUrl ? (
              <div className="w-full space-y-3">
                <div className="p-4 bg-white border border-zinc-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <p className="typo-body-sm font-mono text-[14px] text-zinc-700">{customSnippetsUrl}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full p-4 border-2 border-dashed border-zinc-200 rounded-lg text-center">
                <p className="typo-body-sm text-innoveraZinc-500 text-center">{t("snippets.none")}</p>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            {customSnippetsUrl ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="custom-snippets-url-edit-button"
                  size="sm"
                  onClick={handleEdit}
                  disabled={mutation.isPending}
                  className="flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  {t("snippets.edit")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="custom-snippets-url-clear-button"
                  size="sm"
                  onClick={handleClear}
                  disabled={mutation.isPending}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  {t("snippets.clear")}
                </Button>
              </>
            ) : (
              <Button
                type="button"
                data-testid="custom-snippets-url-configure-button"
                size="sm"
                onClick={handleEdit}
                className="flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                {t("snippets.configure")}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="typo-label-md font-medium text-zinc-700">{t("snippets.urlLabel")}</p>
            <div className="relative flex flex-1 flex-col gap-2">
              <Input
                data-testid="custom-snippets-url-input"
                type="url"
                placeholder="https://your-snippets-service.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={mutation.isPending}
                className="transition-colors"
              />
            </div>
            {url && !isValidUrl(url) && <p className="typo-caption text-red-600 mt-1">{t("snippets.invalidUrl")}</p>}
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              data-testid="custom-snippets-url-save-button"
              size="sm"
              onClick={handleSave}
              disabled={mutation.isPending || (url.trim() !== "" && !isValidUrl(url.trim()))}
              className="flex items-center gap-2"
            >
              {mutation.isPending ? (
                <>
                  <Spinner data-icon="inline-start" aria-label={t("loading", { ns: "common" })} />
                  {t("common.saving")}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {t("snippets.save")}
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="custom-snippets-url-cancel-button"
              size="sm"
              onClick={handleCancel}
              disabled={mutation.isPending}
              className="flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
